import React, { useState, useCallback, useMemo } from "react";
import { DialogContext, type DialogConfig, type DialogContextValue } from "~/contexts/dialogContext";

export interface DialogProviderProps {
  children: React.ReactNode;
}

/**
 * DialogProvider Component
 *
 * Provides dialog state management to the entire application.
 * Should be placed high in the component tree (typically in App.tsx).
 */
export const DialogProvider: React.FC<DialogProviderProps> = ({ children }) => {
  const [dialogState, setDialogState] = useState<DialogConfig | null>(null);

  const openDialog = useCallback((config: DialogConfig) => {
    setDialogState(config);
  }, []);

  const closeDialog = useCallback(() => {
    setDialogState(null);
  }, []);

  const value = useMemo<DialogContextValue>(
    () => ({
      openDialog,
      closeDialog,
      dialogState,
    }),
    [openDialog, closeDialog, dialogState]
  );

  return <DialogContext.Provider value={value}>{children}</DialogContext.Provider>;
};
